(function () {
  'use strict';
  if (window.MWFeatureAudioPlayer) return;

  const instances = new WeakMap();
  const mounts = new WeakMap();
  const selector = '[data-mw-feature-audio]';
  const playIcon = '<svg class="play-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>';
  const pauseIcon = '<svg class="pause-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M7 5h4v14H7zm6 0h4v14h-4z"/></svg>';

  class FeatureAudioPlayer {
    constructor({ root, tracks } = {}) {
      if (!root || !Array.isArray(tracks)) throw new TypeError('A feature root and published audio tracks are required.');
      if (instances.has(root)) return instances.get(root);
      if (typeof window.MWAudioBufferPlayer !== 'function') throw new Error('Audio playback is unavailable.');

      this.root = root;
      this.tracks = tracks.slice();
      this.currentTrackIndex = -1;
      this.destroyed = false;
      this.list = root.querySelector('[data-feature-track-list]');
      this.switch = root.querySelector('.compare-switch');
      this.switchButtons = Array.from(root.querySelectorAll('.switch-button'));
      if (!this.list || !this.switch) throw new Error('Feature audio controls are unavailable.');

      this.populateTracks();
      this.player = new window.MWAudioBufferPlayer({ onChange: () => this.render() });
      this.onClick = event => {
        const button = event.target.closest('button');
        if (!button || !this.root.contains(button)) return;
        if (button.hasAttribute('data-feature-track')) {
          const index = Number(button.dataset.featureTrack);
          this.selectTrack(index);
        } else if (button.classList.contains('switch-button')) {
          this.player.setState(button.dataset.state);
        }
      };
      this.root.addEventListener('click', this.onClick);
      instances.set(root, this);
      this.root.hidden = !this.tracks.length;
      this.render();
      this.ready = Promise.resolve(this);
    }

    populateTracks() {
      const document = this.root.ownerDocument;
      const fragment = document.createDocumentFragment();
      this.trackButtons = this.tracks.map((track, index) => {
        const item = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'mw-feature-audio__track';
        button.dataset.featureTrack = String(index);

        const control = document.createElement('span');
        control.className = 'play-button';
        control.setAttribute('aria-hidden', 'true');
        control.innerHTML = playIcon + pauseIcon;
        const title = document.createElement('span');
        title.className = 'mw-feature-audio__title';
        title.textContent = track.title || track.songTitle || `Example ${index + 1}`;
        button.append(control, title);
        item.append(button);
        fragment.append(item);
        return button;
      });
      this.list.replaceChildren(fragment);
    }

    async selectTrack(index) {
      if (this.destroyed || !this.tracks[index]) return;
      try {
        if (this.currentTrackIndex === index) {
          await this.player.toggle();
        } else {
          this.currentTrackIndex = index;
          this.player.setTrack(this.tracks[index]);
          await this.player.play();
        }
      } catch (_) {
        this.player.pause();
      }
      this.render();
    }

    render() {
      if (this.destroyed || !this.player) return;
      const state = this.player.state;
      this.switch.dataset.state = state;
      this.switchButtons.forEach(button => {
        const selected = button.dataset.state === state;
        button.classList.toggle('active', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
      this.trackButtons.forEach((button, index) => {
        const selected = index === this.currentTrackIndex;
        const playing = selected && this.player.isPlaying;
        const loading = selected && this.player.isLoading;
        const title = button.querySelector('.mw-feature-audio__title').textContent;
        button.classList.toggle('is-selected', selected);
        button.querySelector('.play-button').classList.toggle('playing', playing || loading);
        button.setAttribute('aria-label', `${playing || loading ? 'Pause' : 'Play'} ${title}`);
        button.setAttribute('aria-pressed', String(playing));
        button.setAttribute('aria-busy', String(loading));
      });
    }

    destroy() {
      if (this.destroyed) return;
      this.player.pause();
      this.render();
      this.destroyed = true;
      this.root.removeEventListener('click', this.onClick);
      this.player.destroy();
      instances.delete(this.root);
    }
  }

  function eachRoot(container, callback) {
    if (container.matches && container.matches(selector)) callback(container);
    container.querySelectorAll(selector).forEach(callback);
  }

  function mountWithin(container) {
    eachRoot(container, root => {
      if (mounts.has(root) || !window.MWStorefrontAudio) return;
      const lifecycle = { cancelled: false, instance: null, settled: false };
      mounts.set(root, lifecycle);
      Promise.resolve(window.MWStorefrontAudio.mountFeature({
        root,
        productId: root.dataset.productId,
        audioSetId: root.dataset.audioSetId
      })).then(instance => {
        if (!lifecycle.cancelled) {
          lifecycle.instance = instance;
          lifecycle.settled = true;
        }
      }).catch(() => {
        if (!lifecycle.cancelled) {
          lifecycle.settled = true;
          root.hidden = true;
        }
      });
    });
  }

  function unmountWithin(container) {
    eachRoot(container, root => {
      const lifecycle = mounts.get(root);
      if (!lifecycle) return;
      lifecycle.cancelled = true;
      window.MWStorefrontAudio.unmount(root);
      mounts.delete(root);
    });
  }

  window.MWFeatureAudioPlayer = FeatureAudioPlayer;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mountWithin(document), { once: true });
  } else {
    mountWithin(document);
  }
  document.addEventListener('shopify:section:load', event => mountWithin(event.target));
  document.addEventListener('shopify:section:unload', event => unmountWithin(event.target));
  document.addEventListener('mw:content-ready', () => {
    eachRoot(document, root => {
      const lifecycle = mounts.get(root);
      if (lifecycle && lifecycle.settled && !lifecycle.instance) unmountWithin(root);
    });
    mountWithin(document);
  });
})();
